<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <span
        ref="triggerRef"
        class="a-tooltip"
        @touchstart="onTriggerTouchStart"
        @mouseenter="onTriggerMouseEnter"
        @mouseleave="onTriggerMouseLeave"
        @click="onTriggerClick"
    >
        <div class="tooltip-wrapper">
            <slot>
                <a-glyph v-if="glyph" :name="glyph" />
            </slot>
        </div>
        <teleport to="#teleports" :disabled="!shouldTeleport">
            <transition :name="transition" @after-leave="doDestroy">
                <div
                    v-if="!disabled && showPopper"
                    :key="uniqueKey"
                    ref="dropdownRef"
                    role="tooltip"
                    :class="[
                        'a-popper content',
                        popperClass,
                        maxWidth === 'auto-width' ? 'a-popper--auto-width' : '',
                        visibleArrow ? 'a-popper--with-arrow' : ''
                    ]"
                    :style="computedStyle"
                    :dynamic-width="dynamicWidth"
                    :aria-hidden="disabled || !showPopper ? 'true' : 'false'"
                    @mouseenter="onPopperMouseEnter"
                    @mouseleave="onPopperMouseLeave"
                >
                    <div
                        v-if="visibleArrow"
                        ref="arrowEl"
                        class="a-popper__arrow"
                        data-popper-arrow
                    />

                    <slot name="content">
                        <template v-if="isSimpleContent">
                            <a-dangerous-html class="text" :content="content" />
                        </template>

                        <template v-else-if="isStructuredContent">
                            <div v-if="content.title" class="title">{{ content.title }}</div>
                            <a-dangerous-html v-if="content.content" class="text" :content="content.content" />
                            <template v-if="Array.isArray(content.list)">
                                <ul>
                                    <li v-for="(item, index) in content.list" :key="index">
                                        <a-link v-if="item.to" v-bind="item" />
                                        <div v-else>{{ item.text }}</div>
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </slot>
                </div>
            </transition>
        </teleport>
    </span>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn';
import { usePopper } from '@core/composables/usePopper';

const props = defineProps({
    trigger: { type: String, default: 'hover' },
    openDelay: { type: Number, default: 500 },
    disabled: Boolean,
    manual: Boolean,
    enterable: { type: Boolean, default: true },
    popperClass: { type: String, default: '' },
    content: { type: [String, Number, Object], default: '' },
    visibleArrow: { type: Boolean, default: true },
    transition: { type: String, default: '' },
    popperOptions: { type: Object, default: () => ({ boundariesPadding: 10, gpuAcceleration: false }) },
    hideAfter: { type: Number, default: 0 },
    placement: { type: String, default: 'top' },
    dynamicWidth: { type: Boolean, default: false },
    glyph: { type: String, default: 'tooltip-icon' },
    maxWidth: {
        type: [Number, String],
        default: 325,
        validator: val => typeof val === 'number' && val > 0 || val === 'auto-width',
    },
});

const triggerRef = ref(null);
const dropdownRef = ref(null);
const arrowEl = ref(null);

const shouldTeleport = ref(false);
const showPopper = ref(false);
const expectedState = ref(false);
let timeout = null;
let timeoutPending = null;

const popperOptions = computed(() => ({
    placement: props.placement,
    strategy: 'fixed',
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'arrow', options: { element: arrowEl.value || undefined } },
        { name: 'preventOverflow', options: { boundary: 'viewport' } },
        { name: 'flip', options: { fallbackPlacements: ['top', 'right', 'left', 'bottom'] } },
    ],
    ...props.popperOptions,
}));

const uniqueKey = computed(() => `${Math.random().toString(36).slice(2)}-${Date.now()}`);
const isStructuredContent = computed(() => typeof props.content === 'object' && props.content !== null);
const isSimpleContent = computed(() => typeof props.content === 'string' || typeof props.content === 'number');

const computedStyle = computed(() => {
    return typeof props.maxWidth === 'number' ? { maxWidth: `${props.maxWidth}px` } : {};
});

const { initializePopper, destroyPopper, updatePopper } = usePopper(
    triggerRef,
    dropdownRef,
    popperOptions.value,
);

const { debouncedFn: debounceClose } = useDebouncedFn(handleClosePopper, 200);

function onTriggerTouchStart() {
    if (props.trigger === 'hover' || props.trigger === 'click') show();
}
function onTriggerMouseEnter() {
    if (props.trigger === 'hover') show();
}
function onTriggerMouseLeave() {
    if (props.trigger === 'hover') hide();
}
function onTriggerClick() {
    if (props.trigger === 'click') toggle();
}
function onPopperMouseEnter() {
    if (props.enterable) expectedState.value = true;
}
function onPopperMouseLeave() {
    if (props.enterable) {
        expectedState.value = false;
        debounceClose();
    }
}
function doDestroy() {
    showPopper.value = false;
    nextTick(() => {
        destroyPopper();
    });
}

function show() {
    setExpectedState(true);
    handleShowPopper();
}
function hide() {
    setExpectedState(false);
    debounceClose();
}
function toggle() {
    return expectedState.value ? hide() : show();
}

function handleShowPopper() {
    if (!expectedState.value || props.manual) return;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
        showPopper.value = true;
        nextTick(updatePopper);
    }, props.openDelay);

    if (props.hideAfter > 0) {
        timeoutPending = setTimeout(() => {
            showPopper.value = false;
        }, props.hideAfter);
    }
}

function handleClosePopper() {
    if ((props.enterable && expectedState.value) || props.manual) return;
    clearTimeout(timeout);
    if (timeoutPending) clearTimeout(timeoutPending);
    showPopper.value = false;
}

function setExpectedState(val) {
    if (!val) clearTimeout(timeoutPending);
    expectedState.value = val;
}

watch(showPopper, (visible) => {
    if (visible) {
        nextTick(async () => {
            if (!dropdownRef.value || !triggerRef.value) return;
            await initializePopper();
            requestAnimationFrame(updatePopper);
        });
    }
});

onMounted(() => {
    if (props.manual && showPopper.value) {
        nextTick(async () => {
            await initializePopper();
        });
    }
    requestAnimationFrame(() => {
        shouldTeleport.value = true;
    });
});
onBeforeUnmount(destroyPopper);
</script>

<style scoped lang="postcss">
.a-tooltip {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  .title {
    font-weight: 600;
    margin-bottom: 8px;
  }

  .content {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    &:after {
        content:"";
        display: block;
    }
  }

  .tooltip-wrapper {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
  }
}
</style>
